import {Injectable} from '@angular/core';
import {BackendService} from '@tg4/http-infrastructure/dist/src';
import {Observable} from 'rxjs';
import {map} from 'rxjs/operators';
import {Group} from '../groups/_interfaces/group';
import {HttpClient, HttpHeaders} from '@angular/common/http';
import {AuthService} from '../shared/services/auth.service';
import {UserPreview} from '../shared/interfaces/user-preview.interface';
import {SharedService} from '../shared/services/shared.service';
import {GroupUser} from './_interfaces/group-user.interface';
import {CalibrationFilters} from '../shared/interfaces/calibration-filters.interface';
import { ExportFilter } from '../calibration/_interfaces/export-filters';
import { ConfigService } from '../shared/services/multi-clients/config.service';

@Injectable()
export class GroupsService {
    apiUrl: string;

    constructor(private _httpService: BackendService,
                private authService: AuthService,
                private http: HttpClient,
                private _sharedService: SharedService,
                private _configService: ConfigService
            ) {
        this.apiUrl = this._configService.settings.apiUrl;
    }

    public getCalibrationGroups(cycleId: number): Observable<any> {
        return this._httpService.get('getCalibrationGroups', [], [
            {'name': 'cycleId', 'value': cycleId as any}
        ]);
    }

    public getUsersForCalibration(
        cycleId: number,
        searchString: string = '',
        attributeIds: Array<number> = [],
        page: number = 1,
        onlyNoGroupParticipants: boolean = false,
        finalConcept: Array<string> = [],
        selectedLanguage: string = "",
        pageSize: number = 10
    ): Observable<any> {
        const parameters = [
            {'name': 'cycleId', 'value': cycleId as any},
            {'name': 'search', 'value': searchString},
            {'name': 'page', 'value': page as any},
            {'name': 'pageSize', 'value': pageSize as any},
            {'name': 'onlyNoGroupParticipants', 'value': onlyNoGroupParticipants},
            {'name': 'finalConcept', 'value': finalConcept},
            {'name': 'selectedLanguage', 'value': selectedLanguage},
        ];

        if (attributeIds && attributeIds.length > 0)
            parameters.push(...attributeIds.map(x => <any>{'name': 'attributeIds', 'value': x}));

        return this._httpService.get('getUsersForCalibration', [], parameters);
    }

    public getAllUsersForCalibration(
        cycleId: number,
        searchString: string = '',
        attributeIds: Array<number> = [],
        onlyNoGroupParticipants: boolean = false,
        finalConcept: Array<string> = [],
        selectedLanguage: string = "",
    ): Observable<any> {
        const parameters = [
            {'name': 'cycleId', 'value': cycleId as any},
            {'name': 'search', 'value': searchString},
            {'name': 'finalConcept', 'value': finalConcept},
            {'name': 'onlyNoGroupParticipants', 'value': onlyNoGroupParticipants},
            {'name': 'selectedLanguage', 'value': selectedLanguage},
        ];

        if (attributeIds && attributeIds.length > 0)
            parameters.push(...attributeIds.map(x => <any>{'name': 'attributeIds', 'value': x}));

        return this._httpService.get('getUsersForCalibration', [], parameters);
    }

    public getUsersToShareCalibrationGroup(
        cycleId: number, searchString: string = '',
        attributeIds: Array<number> = [],
        page: number = 1, pageSize: number = 10
    ): Observable<any> {
        return this._httpService.get('getUsersToShareCalibrationGroup', [], [
            {'name': 'cycleId', 'value': cycleId as any},
            {'name': 'search', 'value': searchString},
            {'name': 'attributeIds', 'value': attributeIds as any},
            {'name': 'page', 'value': page as any},
            {'name': 'pageSize', 'value': pageSize as any}
        ]);
    }

    public getCalibrationGroupById(groupId: number): Observable<any> {
        return this._httpService.get('getCalibrationGroupById', [], [
            {'name': 'groupId', 'value': groupId as any},
        ]);
    }

    public exportGroups(
        cycleId: number,
        groupId: number = null,
        filters: CalibrationFilters = null): Observable<any> {
        return this.downloadFile({
            cycleId: cycleId,
            groupId: groupId,
            goalsFinalEvaluationFilters: filters.goalsFinalEvaluationFilters,
            principlesFinalEvaluationFilters: filters.principlesFinalEvaluationFilters,
            managerFinalEvaluationFilters: filters.managerFinalEvaluationFilters,
            calibrationFinalEvaluationFilters: filters.calibrationFinalEvaluationFilters,
            attributeIds: filters.attributeIds,
            hireDateStart: filters.hireDateStart,
            hireDateEnd: filters.hireDateEnd,
            serviceDateStart: filters.serviceDateStart,
            serviceDateEnd: filters.serviceDateEnd,
            finalEvaluationChanged: filters.finalEvaluationChanged,
            hasComment: filters.hasComment,
            managerFilters: filters.managerFilters,
            searchString: filters.searchString,
            orderBy: filters.orderBy,
            orderDescending: filters.orderDescending,
        });
    }

    downloadFile(data: any): any {
        const applicationsUrl = `${this.apiUrl}/api/calibration/exportGroups`;
        const user = this.authService.getLoggedUser();
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': user.access_token,
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            observe: 'response' as 'body',
            responseType: 'blob' as 'json'
        };

        const impersonatedUser: UserPreview | null = this.authService.getImpersonatedUser();
        if (impersonatedUser && impersonatedUser.userId) {
            options.headers['UserIdToImpersonate'] = impersonatedUser.userId;
        }
        return this.http.post(applicationsUrl, data, options)
            .pipe(map((res) => this._sharedService.downloadFile(res)));
    }

    downloadGroupExport(groupId: number): any {
        const applicationsUrl = `${this.apiUrl}/api/calibration/group/participants/comparisonExport?groupId=${groupId}`;
        const user = this.authService.getLoggedUser();
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': user.access_token,
                'Accept': 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            }),
            observe: 'response' as 'body',
            responseType: 'blob' as 'json'
        };

        const impersonatedUser: UserPreview | null = this.authService.getImpersonatedUser();
        if (impersonatedUser && impersonatedUser.userId) {
            options.headers['UserIdToImpersonate'] = impersonatedUser.userId;
        }
        return this.http.get(applicationsUrl, options)
            .pipe(map((res) => this._sharedService.downloadFile(res)));
    }

    public getHasReviewAccess(userId: string): Observable<any> {
        return this._httpService.get('hasAccessToReview', [], [
            {'name': 'userId', 'value': userId as any}
        ]);
    }

    public postGroupExport(fileToUpload: string, groupId): Observable<any> {
        const data = {
            groupId: groupId,
            fileContent: fileToUpload
        }
        return this._httpService.post('postCalibrationGroupExport', data);
    }

    public updateResumeGroup(fileToUpload: string, groupId): Observable<any> {
        const data = {
            groupId: groupId,
            fileContent: fileToUpload
        }
        return this._httpService.post('updateResumeGroup', data);
    }

    public exportExecutiveReportPresentation(
        cycleId: number,
        groupId: number,
        includeEvaluator: boolean,
        language: string,
        columnFilters: ExportFilter,
        fontSize: number,
        fontSizeComment: number,
        filters: CalibrationFilters): Observable<any> {
        return this.downloadExecutiveReportPresentation({
            cycleId: cycleId,
            groupId: groupId,
            includeEvaluator: includeEvaluator,
            columnFilters: columnFilters,
            language: language,
            fontSize: fontSize,
            fontSizeComment: fontSizeComment,
            filters: {
                goalsFinalEvaluationFilters: filters.goalsFinalEvaluationFilters,
                principlesFinalEvaluationFilters: filters.principlesFinalEvaluationFilters,
                managerFinalEvaluationFilters: filters.managerFinalEvaluationFilters,
                calibrationFinalEvaluationFilters: filters.calibrationFinalEvaluationFilters,
                attributeIds: filters.attributeIds,
                hireDateStart: filters.hireDateStart,
                hireDateEnd: filters.hireDateEnd,
                serviceDateStart: filters.serviceDateStart,
                serviceDateEnd: filters.serviceDateEnd,
                finalEvaluationChanged: filters.finalEvaluationChanged,
                hasComment: filters.hasComment,
                managerFilters: filters.managerFilters,
                searchString: filters.searchString,
                orderBy: filters.orderBy,
                orderDescending: filters.orderDescending,
            }
        });
    }

    downloadExecutiveReportPresentation(data: any): any {
        const applicationsUrl =
            `${this.apiUrl}/api/report/executive/${data.cycleId}/${data.groupId}/pdf?includeEvaluator=${data.includeEvaluator}`;

        const user = this.authService.getLoggedUser();
        const options = {
            headers: new HttpHeaders({
                'Content-Type': 'application/json',
                'Authorization': user.access_token
            }),
        };

        const impersonatedUser: UserPreview | null = this.authService.getImpersonatedUser();
        if (impersonatedUser && impersonatedUser.userId) {
            options.headers['UserIdToImpersonate'] = impersonatedUser.userId;
        }
        return this.http.post(applicationsUrl, data, options);
    }

    public createCalibrationGroup(group: Group): Observable<any> {
        return this._httpService.post('createCalibrationGroup', group);
    }

    public updateCalibrationGroup(group: Group): Observable<any> {
        return this._httpService.put('updateCalibrationGroup', group);
    }

    public deleteCalibrationGroup(groupId: number): Observable<any> {
        return this._httpService.delete('deleteCalibrationGroup', [
            {'name': 'groupId', 'value': groupId as any}
        ]);
    }

    public leaveSharedCalibrationGroup(groupId: number): Observable<any> {
        return this._httpService.post('leaveSharedCalibrationGroup', {
            'groupId': groupId
        });
    }

    public shareCalibrationGroup(groupId: number, users: Array<GroupUser>): Observable<any> {
        return this._httpService.put('shareCalibrationGroup', {
            'groupId': groupId,
            'usersToUpdate': users
        });
    }

    public resetNineBoxCalibrationGroup(groupId: number, cycleId: number): Observable<any> {
        return this._httpService.put('resetNineBoxCalibrationGroup', {
            'groupId': groupId,
            'cycleId': cycleId
        });
    }
}
